import type { TrafficShareTab } from '../pages/company-overview/TrafficShare/TrafficShare';
import type { TrafficVisitsTab } from '../pages/company-overview/TrafficVisits/TrafficVisits';

import type { WidgetItem } from './company_widgets';
import type { EmailLengthText } from './generatedEmail';
import type { Quota } from './quota';

export type ContactsQuota = Quota & { isFeatureLocked: boolean };

export type ExportQuota = Quota;

export type UserIndustry =
  | 'Ad tech & Ad network'
  | 'Consulting and market research'
  | 'B2B software'
  | 'Payments'
  | 'Logistics and shipping'
  | 'Agency'
  | 'Private equity and venture capital'
  | 'Investors';

export enum UserRole {
  USER = 'User',
  ADMIN = 'Admin',
  CHAMPION = 'Champion',
}

export enum ContactsAvailabilityStatus {
  FullAccess = 1,
  Readonly = 2,
  PendingApproval = 3,
  DisabledByCountry = 4,
  DisabledByAccount = 5,
}

export type User = {
  userId: number;
  accountId: number;
  accountName: string;
  firstName: string;
  lastName: string;
  email: string;
  contactsQuota: ContactsQuota;
  isExtensionEnabled: boolean;
  isImpersonate: boolean;
  isTrialAccount: boolean;
  isAfterTrialAccount: boolean;
  isNoTouchAccount: boolean;
  accountType: string;
  uiVersion: string;
  hasAccessToManageCrm: boolean;
  isInternalUser: boolean;
  userRole: UserRole;
  phone?: string;
  opportunitiesLimit: number;
  availableCountries: number[];
  accountIndustry?: UserIndustry;
  isRegistrationQuizCompleted: boolean;
  isCrmFeatureEnabled?: boolean;
  accountAdminEmails?: string[];
  userSettings?: UserSettings;
  contactsAvailability: ContactsAvailabilityStatus;
  isMeetingAgentFeatureEnabled?: boolean;
  isCraftEmailAgentFeatureEnabled?: boolean;
  isDataCreditsEnabled?: boolean;
};

export type UserWithStringifiedSettings = User & {
  userSettings?: Record<string, string>;
};

export const isAdmin = (user?: User): boolean => !!user && user.userRole === UserRole.ADMIN;

export const COMPANY_OVERVIEW_FULL_ACCESS = 'hasRequestedOverviewFullAccess';
export const INSIGHTS_LIMIT_INCREASE = 'hasRequestedInsightsFullAccess';
export const CONTACTS_FULL_ACCESS = 'hasRequestedContactsFullAccess';
export const CONTACTS_LIMIT_INCREASE = 'hasRequestedContactsLimitIncrease';
export const DATA_CREDITS_ACCESS = 'hasRequestedDataCreditsAccess';
export const SAM_LANGUAGE_CODE = 'samLanguageCode';
export const IS_SAM_PREFERENCES_TOOLTIP_SHOWN = 'isSamPreferencesTooltipShown';
export const IS_SAM_LANGUAGE_TOOLTIP_SHOWN = 'isSamLanguageTooltipShown';
export const SELECTED_TECH_CATEGORIES = 'selectedTechCategories';
export const SELECTED_COMPANY_OVERVIEW_WIDGETS = 'companyOverviewWidgets';
export const TRAFFIC_SHARE_TAB_KEY = 'trafficShareTab';
export const TRAFFIC_VISITS_TAB_KEY = 'trafficVisitsTab';
export const USER_SAM_ON_BOARDING_COMPLETED = 'userSamOnBoardingCompleted';
export const USER_SAM_EMAIL_LENGTH_TEXT = 'samEmailLengthText';

export type UserSettingsKey = keyof UserSettings;

export type UserHasRequestedAccessSettings = {
  [COMPANY_OVERVIEW_FULL_ACCESS]?: boolean;
  [INSIGHTS_LIMIT_INCREASE]?: boolean;
  [CONTACTS_FULL_ACCESS]?: boolean;
  [CONTACTS_LIMIT_INCREASE]?: boolean;
  [DATA_CREDITS_ACCESS]?: boolean;
};

export type UserHasRequestedAccessSettingsKey = keyof UserHasRequestedAccessSettings;

export type UserSettings = {
  [SELECTED_COMPANY_OVERVIEW_WIDGETS]?: WidgetItem[];
  [SAM_LANGUAGE_CODE]?: string;
  [IS_SAM_PREFERENCES_TOOLTIP_SHOWN]?: boolean;
  [IS_SAM_LANGUAGE_TOOLTIP_SHOWN]?: boolean;
  [SELECTED_TECH_CATEGORIES]?: string[];
  [TRAFFIC_SHARE_TAB_KEY]?: TrafficShareTab;
  [TRAFFIC_VISITS_TAB_KEY]?: TrafficVisitsTab;
  [USER_SAM_ON_BOARDING_COMPLETED]?: boolean;
  [USER_SAM_EMAIL_LENGTH_TEXT]?: EmailLengthText;
};

export const isUserSettingsKey = (key: string): boolean => {
  return [
    SELECTED_COMPANY_OVERVIEW_WIDGETS,
    SAM_LANGUAGE_CODE,
    IS_SAM_PREFERENCES_TOOLTIP_SHOWN,
    IS_SAM_LANGUAGE_TOOLTIP_SHOWN,
    SELECTED_TECH_CATEGORIES,
    TRAFFIC_SHARE_TAB_KEY,
    TRAFFIC_VISITS_TAB_KEY,
    USER_SAM_ON_BOARDING_COMPLETED,
    USER_SAM_EMAIL_LENGTH_TEXT,
  ].includes(key);
};
